import { useCallback } from 'react';

import { useNavigateToRoute } from '@/components/navigation/useNavigateToRoute';
import { useTrackUserEvent } from '@/hooks/useTrackUserEvent';
import {
  ENTITY_TYPES,
  NEW_ENTITY_SENTINEL,
} from '@/modules/entities/entities.constants';
import { NEW_PROPOSAL_ID } from '@/modules/proposal/proposal.constants';
import {
  GiftDesignerStages,
  GratDesignerStages,
  ROUTE_KEYS,
} from '@/navigation/constants';

export interface AnalysisActions {
  handleGratClick: () => void;
  handleGiftingClick: () => void;
  handleCharitableDesignerClick: (variant: 'crt' | 'clt') => () => void;
}

export function useAnalysisActions(householdId: string): AnalysisActions {
  const { navigate } = useNavigateToRoute();
  const trackUserEvent = useTrackUserEvent();

  const handleGratClick = useCallback(() => {
    trackUserEvent('proposal created grat');
    navigate(ROUTE_KEYS.HOUSEHOLD_ENTITY_DESIGNER, {
      householdId,
      entityId: NEW_ENTITY_SENTINEL,
      designerStage: GratDesignerStages.BASIC_INFORMATION,
      entityType: ENTITY_TYPES.GRAT,
    });
  }, [householdId, navigate, trackUserEvent]);

  const handleGiftingClick = useCallback(() => {
    trackUserEvent('proposal created gifting');
    navigate(ROUTE_KEYS.HOUSEHOLD_GIFT_DESIGNER, {
      householdId,
      proposalId: NEW_PROPOSAL_ID,
      designerStage: GiftDesignerStages.BASIC_INFORMATION,
    });
  }, [householdId, navigate, trackUserEvent]);

  const handleCharitableDesignerClick = useCallback(
    (variant: 'crt' | 'clt') => {
      return () => {
        trackUserEvent(`proposal created ${variant}`);
        navigate(
          ROUTE_KEYS.HOUSEHOLD_GIFT_CHARITABLE_TRUST_DESIGNER_BASIC_INFORMATION,
          { householdId, proposalId: NEW_PROPOSAL_ID, variant }
        );
      };
    },
    [householdId, navigate, trackUserEvent]
  );

  return {
    handleGratClick,
    handleGiftingClick,
    handleCharitableDesignerClick,
  };
}
